import PropTypes from "prop-types";
import * as React from "react";
import { connect, Provider, ReactReduxContextValue } from "react-redux";
import { Action, createStore, Store, Unsubscribe } from "redux";

import { pullContext } from "../../TypingUtils";

export type ContextDispatch<_, TAction> = (action: TAction) => TAction;

interface IReduxBasedContextInstance<TState, TAction> {
    Root: React.ComponentType<ReduxBasedContextRootProps<TState>>;
    ConnectToState: React.ComponentType<ReduxBasedConnectToStateProps<TState>>;
    ConnectToActions: React.ComponentType<ReduxBasedConnectToActionsProps<TState, TAction>>;
}

interface ReduxBasedContextRootProps<TState> {
    initialState?: TState;
    children?: JSX.Element;
}

interface ReduxBasedConnectToStateProps<TState> {
    children?: (state: TState) => JSX.Element | null;
}

interface ReduxBasedConnectToActionsProps<TState, TAction> {
    children?: (dispatch: ContextDispatch<TState, TAction>) => JSX.Element | null;
}

interface StickyTableWrapperContextPull {
    recalcHeightRows?: () => void;
}

export function createReduxBasedContext<TState, TAction extends Action>(
    reducer: (state: TState, action: TAction) => TState
): IReduxBasedContextInstance<TState, TAction> {
    // @ts-ignore Не придумал как кинуть исключение если контекст не создали
    const NewContext = React.createContext<ReactReduxContextValue>(null);

    class ReduxBasedContextRoot extends React.Component<ReduxBasedContextRootProps<TState>> {
        public static contextTypes = { recalcHeightRows: PropTypes.func };
        private readonly store: Store<TState>;
        private readonly unsubscribe: Unsubscribe;

        public constructor(props: ReduxBasedContextRootProps<TState>) {
            super(props);
            // @ts-ignore
            this.store = createStore(reducer, this.props.initialState);
            this.unsubscribe = this.store.subscribe(() => {
                setTimeout(() => {
                    const recalcHeightRows = this.getContext().recalcHeightRows;
                    if (recalcHeightRows != undefined) {
                        recalcHeightRows();
                    }
                }, 1);
            });
        }

        public componentWillUnmount() {
            this.unsubscribe();
        }

        public render(): JSX.Element {
            return (
                <Provider context={NewContext} store={this.store}>
                    {this.props.children}
                </Provider>
            );
        }

        private getContext(): StickyTableWrapperContextPull {
            return pullContext<StickyTableWrapperContextPull>(this);
        }
    }

    function PureReduxBasedConnectToState(props: {
        children?: (state: TState) => JSX.Element | null;
        state: TState;
    }): JSX.Element | null {
        if (props.children == undefined) {
            return null;
        }
        return props.children(props.state);
    }

    const ReduxBasedConnectToState = connect(
        (state: TState) => ({ state: state }),
        () => ({}),
        null,
        {
            context: NewContext,
        }
    )(PureReduxBasedConnectToState);

    function PureReduxBasedConnectToActions(props: {
        children?: (state: ContextDispatch<TState, TAction>) => JSX.Element | null;
        dispatch: ContextDispatch<TState, TAction>;
    }): JSX.Element | null {
        if (props.children == undefined) {
            return null;
        }
        return props.children(props.dispatch);
    }

    const ReduxBasedConnectToActions = connect(
        () => ({}),
        (dispatch: ContextDispatch<TState, TAction>) => ({ dispatch: dispatch }),
        null,
        {
            context: NewContext,
        }
    )(PureReduxBasedConnectToActions);

    return {
        Root: ReduxBasedContextRoot,
        ConnectToState: ReduxBasedConnectToState,
        ConnectToActions: ReduxBasedConnectToActions,
    };
}
